import { useEffect, useRef, useState } from 'react';
import { Button, Grid, Modal, Space, Spin, Tooltip, Typography, message } from 'antd';
import { BellOutlined, CreditCardOutlined, MailOutlined, NotificationOutlined, PlusOutlined, ReadOutlined, SmileOutlined } from '@ant-design/icons';
import { EmailTemplatePurpose } from '../../Types/EmailTemplatePurpose';
import { TextService } from '../../Services/TextService';
import { EmailTemplateService } from '../../Services/EmailTemplateService';
import { WebApiServicesDataTransferObjectsEmailServiceOneFrontEmailTemplateDto } from '../../Repository/eventbookyapi';
import pingIcon from '../../Images/pingpayments-icon.png';

type OneFrontTemplate = WebApiServicesDataTransferObjectsEmailServiceOneFrontEmailTemplateDto;

const DEFAULT_ACCENT_COLOR = "#4a6cf7";

// The groups of the list, in the order they are shown. A template belongs to the first group its key starts with.
const CATEGORIES = [
    { textId: "OneFrontTemplates_CategoryWelcome", keyPrefix: "welcome", icon: <SmileOutlined /> },
    { textId: "OneFrontTemplates_CategoryAdmin", keyPrefix: "admin", icon: <NotificationOutlined /> },
    { textId: "OneFrontTemplates_CategoryMembershipFee", keyPrefix: "membership-fee", icon: <CreditCardOutlined /> },
    { textId: "OneFrontTemplates_CategoryReminder", keyPrefix: "payment-reminder", icon: <BellOutlined /> },
    { textId: "OneFrontTemplates_CategoryNewsletter", keyPrefix: "newsletter", icon: <ReadOutlined /> },
    { textId: "OneFrontTemplates_CategoryOther", keyPrefix: "", icon: <MailOutlined /> },
];

const getCategory = (template: OneFrontTemplate) =>
    CATEGORIES.find(p => (template.key ?? "").startsWith(p.keyPrefix)) ?? CATEGORIES[CATEGORIES.length - 1];

interface OneFrontTemplateGalleryProps {
    open: boolean;
    title: string;
    automation?: boolean;
    enablePaymentLink: boolean;
    onClose: () => void;
    onCreateBlank: () => void;
    onUseTemplate: (template: OneFrontTemplate) => void;
}

// Some environments return the preview HTML as a JSON-encoded string.
const normalizePreviewHtml = (content: string) => {
    const trimmedContent = content?.trim();
    if (trimmedContent?.startsWith('"') && trimmedContent.endsWith('"')) {
        try {
            const parsedContent = JSON.parse(trimmedContent);
            if (typeof parsedContent === "string") {
                return parsedContent;
            }
        } catch {
            return content;
        }
    }

    return content;
}

/**
 * The standard templates of OneFront: a list grouped by category, and a preview of the chosen template
 * beside it. On a phone the list is shown alone and the preview opens on top of it.
 */
export const OneFrontTemplateGallery = (props: OneFrontTemplateGalleryProps) => {
    const GetText = TextService.GetText;
    const isMobile = !Grid.useBreakpoint().md;
    const [templates, setTemplates] = useState([] as OneFrontTemplate[]);
    const [selected, setSelected] = useState(undefined as OneFrontTemplate | undefined);
    const [isMobilePreviewOpen, setIsMobilePreviewOpen] = useState(false);
    // The rendered preview of each template, so going back to one does not ask the server again.
    const [previews, setPreviews] = useState({} as Record<string, string>);
    const selectedKey = useRef<string | undefined>(undefined);

    // Under "Automatiska utskick" only the templates made for an automatic sending are offered, and a
    // notice to the administrator is never sent to members by hand. Templates with personal payment links
    // are only offered when the company has Ping Payments, as the links are empty without it.
    const visibleTemplates = (props.automation
        ? templates.filter(p => !!p.purpose)
        : templates.filter(p => (p.purpose as number | undefined) !== EmailTemplatePurpose.MemberGetsActivatedNotification))
        .filter(p => !p.requiresPaymentLink || props.enablePaymentLink);
    const groups = CATEGORIES
        .map(category => ({ category, templates: visibleTemplates.filter(p => getCategory(p) === category) }))
        .filter(p => p.templates.length > 0);

    useEffect(() => {
        if (props.open && templates.length === 0) {
            EmailTemplateService.GetOneFrontTemplates().then(result => setTemplates(result));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.open]);

    // The empty template is chosen each time the chooser opens, as it is first in the list.
    useEffect(() => {
        if (props.open) {
            selectedKey.current = undefined;
            setSelected(undefined);
        }
    }, [props.open]);

    const onSelectBlank = () => {
        selectedKey.current = undefined;
        setSelected(undefined);

        // On a phone there is no pane beside the list, so the empty template opens right away.
        if (isMobile) {
            onCreateBlank();
        }
    }

    const onCreateBlank = () => {
        props.onClose();
        props.onCreateBlank();
    }

    const onSelect = async (template: OneFrontTemplate) => {
        const key = template.key ?? "";
        selectedKey.current = key;
        setSelected(template);

        if (isMobile) {
            setIsMobilePreviewOpen(true);
        }

        if (previews[key]) {
            return;
        }

        try {
            const result = await EmailTemplateService.PreviewEmail({
                webApiMemberDtoPreviewMailBodyDto: {
                    message: template.body ?? "",
                    design: template.design,
                }
            });
            setPreviews(previous => ({ ...previous, [key]: normalizePreviewHtml(result) || template.body || "" }));
        } catch {
            // Only tell about the template still looked at, not one clicked past.
            if (selectedKey.current === key) {
                message.error(GetText("EmailForm_PreviewFailed"));
            }
        }
    }

    const onUse = (template: OneFrontTemplate) => {
        setIsMobilePreviewOpen(false);
        props.onClose();
        props.onUseTemplate(template);
    }

    const renderPingIcon = (template: OneFrontTemplate) => template.requiresPaymentLink &&
        <Tooltip title={GetText("OneFrontTemplates_UsesPingPayments")}>
            <img className="onefront-template-ping" src={pingIcon} alt="Ping Payments" />
        </Tooltip>;

    const renderList = () =>
        <div className="onefront-template-list">
            <button
                type="button"
                className={"onefront-template-row onefront-template-blank" + (!selected ? " is-selected" : "")}
                onClick={onSelectBlank}
            >
                <span className="onefront-template-icon onefront-template-blank-icon"><PlusOutlined /></span>
                <span className="onefront-template-text">
                    <span className="onefront-template-name">{GetText("OneFrontTemplates_Blank")}</span>
                    <span className="onefront-template-description">{GetText("OneFrontTemplates_BlankDescription")}</span>
                </span>
            </button>

            {templates.length === 0 && <div style={{ padding: "24px", textAlign: "center" }}><Spin /></div>}

            {groups.map(group =>
                <div key={group.category.textId} className="onefront-template-group">
                    <div className="onefront-template-group-title">{GetText(group.category.textId)}</div>
                    {group.templates.map(template => {
                        const accentColor = template.design?.accentColor || DEFAULT_ACCENT_COLOR;
                        const isSelected = template.key === selected?.key;

                        return <button
                            type="button"
                            key={template.key}
                            className={"onefront-template-row" + (isSelected ? " is-selected" : "")}
                            onClick={() => onSelect(template)}
                        >
                            <span className="onefront-template-icon" style={{ color: accentColor, backgroundColor: accentColor + "1a" }}>{group.category.icon}</span>
                            <span className="onefront-template-text">
                                <span className="onefront-template-name">{template.name}</span>
                                <span className="onefront-template-description">{template.description}</span>
                            </span>
                            {renderPingIcon(template)}
                        </button>;
                    })}
                </div>
            )}
        </div>;

    const renderPreview = (template: OneFrontTemplate, height: number) => {
        const html = previews[template.key ?? ""];

        return <div className="onefront-template-preview">
            <div className="onefront-template-preview-head">
                <div style={{ minWidth: 0 }}>
                    <Typography.Text strong>{template.name}</Typography.Text>
                    {/* The list cuts the description short, so it is shown in full here. */}
                    <div className="onefront-template-preview-description">{template.description}</div>
                    <div className="onefront-template-description"><b>{GetText("CreateTemplate_Subject")}:</b> {template.subject}</div>
                </div>
                <Button type="primary" onClick={() => onUse(template)}>{GetText("OneFrontTemplates_Use")}</Button>
            </div>
            {!html
                ? <div className="onefront-template-frame" style={{ height, display: "flex", alignItems: "center", justifyContent: "center" }}><Spin /></div>
                : <iframe
                    key={template.key}
                    className="onefront-template-frame"
                    title={GetText("OneFrontTemplates_Preview")}
                    srcDoc={html}
                    sandbox=""
                    style={{ height }}
                />}
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>{GetText("OneFrontTemplates_PreviewHint")}</Typography.Text>
        </div>;
    }

    const renderBlank = () =>
        <div className="onefront-template-blank-pane">
            <span className="onefront-template-icon onefront-template-blank-icon onefront-template-blank-icon-large"><PlusOutlined /></span>
            <Typography.Title level={5} style={{ margin: 0 }}>{GetText("OneFrontTemplates_Blank")}</Typography.Title>
            <Typography.Text type="secondary">{GetText("OneFrontTemplates_BlankInfo")}</Typography.Text>
            <Button type="primary" onClick={onCreateBlank}>{GetText("OneFrontTemplates_CreateBlank")}</Button>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>{GetText("OneFrontTemplates_BlankHint")}</Typography.Text>
        </div>;

    return <>
        <Modal
            width={1080}
            open={props.open}
            title={props.title}
            onCancel={props.onClose}
            footer={null}
        >
            <Typography.Paragraph type="secondary">{GetText("OneFrontTemplates_Description")}</Typography.Paragraph>

            {isMobile
                ? renderList()
                : <div className="onefront-template-layout">
                    {renderList()}
                    {selected ? renderPreview(selected, 520) : renderBlank()}
                </div>}
        </Modal>

        {isMobile && <Modal
            open={isMobilePreviewOpen && !!selected}
            title={selected?.name}
            onCancel={() => setIsMobilePreviewOpen(false)}
            footer={<Space><Button onClick={() => setIsMobilePreviewOpen(false)}>{GetText("Common_Close")}</Button></Space>}
        >
            {selected && renderPreview(selected, 460)}
        </Modal>}
    </>;
};

export default OneFrontTemplateGallery;
